/**
 * The admin session contract: the login/refresh payload, its normalized `SessionData`, expiry, the
 * single-flight refresh and the storage-key filter. Pure — no React, no axios, no localStorage.
 *
 * KEEP IN SYNC with the legacy admin's copy (`Core/client/admin/src/shared/auth`: `types.ts`,
 * `session/parse-session.js`, `session/is-expired.js`, `session/storage-keys.js`,
 * `refresh/single-flight.js`). Both admins read and write the same `usrs_*` / `perm_*` /
 * `usrs_spmods` localStorage keys, so a session is shared between them when they are served from
 * the same origin; a field or key changed on one side only silently breaks that. This file was the
 * `@sleed/bizcommerce-auth` workspace package until each admin took its own copy.
 */

/**
 * The raw server payload from `POST /account/login` and `POST /account/refreshtoken` (terse server
 * codes). Kept loose because the server may send extra fields the client ignores.
 */
export interface LoginResponse {
	id?: string | number;
	uid?: string;
	eml?: string;
	nm?: string;
	lm?: string;
	at?: string;
	rt?: string;
	ad?: string | number;
	rd?: string | number;
	redirect_url?: string;
	role?: Record<string, unknown> | null;
	spmods?: string[] | null;
	[k: string]: unknown;
}

/** The normalized session. The storage adapter maps it to/from the `usrs_*` / `perm_*` / `usrs_spmods` keys. */
export interface SessionData {
	id: string | null;
	uid: string | null;
	email: string | null;
	firstName: string | null;
	lastName: string | null;
	accessToken: string | null;
	refreshToken: string | null;
	accessTokenDurationMinutes: string | null;
	refreshTokenDurationMinutes: string | null;
	/** ISO 8601 going forward; the legacy `Date.toString()` format is also accepted on read. */
	loginDate: string | null;
	/** `perm_*` flag map, keys verbatim (e.g. `{ perm_cms: 'true' }`). */
	perms: Record<string, string>;
	/** Parsed `usrs_spmods` (the `;`-joined string split into a list). */
	specialModules: string[];
	loginRedirectUrl: string | null;
}

/** Storage seam: the session store never touches `localStorage` directly. */
export interface SessionStorageAdapter {
	/** Read the persisted session on boot. `null` when there is no session. */
	load(): SessionData | null;
	/** Persist after login / refresh. */
	save(data: SessionData): void;
	/** Remove the session on logout (must NOT touch unrelated keys like `I18N_LANGUAGE`). */
	clear(): void;
	/** Subscribe to external storage changes (multi-tab). Returns an unsubscribe fn. */
	subscribe(cb: () => void): () => void;
}

/**
 * Normalize a raw login/refresh response into a `SessionData`. Only `perm_*` keys are pulled from
 * `raw.role`; `spmods` is kept as a list; `loginDate` is stamped to `now` (on login AND refresh).
 */
export const parseSession = (raw: LoginResponse | null | undefined, now: Date = new Date()): SessionData | null => {
	if (raw == null) return null;

	const perms: Record<string, string> = {};
	if (raw.role != null && typeof raw.role === 'object') {
		for (const [key, value] of Object.entries(raw.role)) {
			if (key.startsWith('perm_')) perms[key] = String(value);
		}
	}

	const specialModules = Array.isArray(raw.spmods) ? raw.spmods.slice() : [];
	const loginDate = now instanceof Date && !Number.isNaN(now.getTime()) ? now.toISOString() : null;

	return {
		id: raw.id != null ? String(raw.id) : null,
		uid: raw.uid != null ? String(raw.uid) : null,
		email: raw.eml ?? null,
		firstName: raw.nm ?? null,
		lastName: raw.lm ?? null,
		accessToken: raw.at ?? null,
		refreshToken: raw.rt ?? null,
		accessTokenDurationMinutes: raw.ad != null ? String(raw.ad) : null,
		refreshTokenDurationMinutes: raw.rd != null ? String(raw.rd) : null,
		loginDate,
		perms,
		specialModules,
		loginRedirectUrl: raw.redirect_url ?? null
	};
};

/**
 * True if the session is missing, corrupt, or past its refresh-token expiry
 * (`loginDate + refreshTokenDurationMinutes`). Corrupt (NaN) inputs count as expired.
 */
export const isSessionExpired = (session: SessionData | null | undefined, now: Date = new Date()): boolean => {
	if (session == null || session.loginDate == null) return true;

	const loginMs = new Date(session.loginDate).getTime();
	const minutes = parseInt(session.refreshTokenDurationMinutes ?? '', 10);
	if (Number.isNaN(loginMs) || Number.isNaN(minutes)) return true;

	const nowMs = now instanceof Date ? now.getTime() : new Date(now).getTime();
	if (Number.isNaN(nowMs)) return true;

	return nowMs > loginMs + minutes * 60 * 1000;
};

/**
 * Multi-tab storage filter: true if a `StorageEvent` key is auth-relevant (`null` means
 * `localStorage.clear()`). Unrelated writes (I18N_LANGUAGE, theme, active_site_*) are ignored.
 */
export const isAuthStorageKey = (key: string | null): boolean =>
	key == null || key.startsWith('usrs_') || key.startsWith('perm_') || key === 'usrs_spmods';

/**
 * Single-flight token refresh. Concurrent callers (e.g. parallel requests that all 401) await the
 * SAME in-flight promise, so exactly one `post` is issued. The shared slot is cleared once settled
 * (success or failure) without adding a catch to the returned promise, so awaiters still observe the
 * rejection and a later expiry can refresh again.
 *
 * `getSessionGeneration` (optional) guards logout-during-refresh: if the generation changed while
 * the flight was in progress, `onSuccess` is skipped for all awaiters (they still receive the data).
 */
export const createSingleFlightRefresh = <T>({
	post,
	onSuccess,
	getSessionGeneration
}: {
	post: () => Promise<T>;
	onSuccess?: (data: T) => void;
	getSessionGeneration?: () => number;
}): (() => Promise<T>) => {
	let sharedRefreshPromise: Promise<T> | null = null;

	return () => {
		if (!sharedRefreshPromise) {
			const generationAtStart = getSessionGeneration ? getSessionGeneration() : null;
			const flight = post().then(data => {
				if (!data) throw new Error('refresh token: empty response');
				const racedByLogout = getSessionGeneration && getSessionGeneration() !== generationAtStart;
				if (onSuccess && !racedByLogout) onSuccess(data);
				return data;
			});
			sharedRefreshPromise = flight;
			flight
				.catch(() => {})
				.then(() => {
					sharedRefreshPromise = null;
				});
		}
		return sharedRefreshPromise;
	};
};
